import { t } from '@lingui/core/macro'

import { cmmsCurrency, formatCmmsMoney } from './cmmsMoney'
import sourceLabels from './cmmsRecordSourceLabels.json'
import { CMMS_NUMBER_FIELDS, type CmmsRecordType } from './cmmsRecordTypes'
import { cmmsRecordNumber, type WorkOrderEntry } from './workOrder'
import type { WorkOrderPdf } from './workOrderPdf'
import type { WorkOrderField } from './workOrderPresentation'
import { WorkOrderSheet } from './workOrderSheet'
import { workOrderPlainText } from './workOrderText'

function businessFields() {
  return [
    ['polinenum', t`Line number`],
    ['prlinenum', t`Line number`],
    ['invuselinenum', t`Line number`],
    ['seqnum', t`Sequence`],
    ['sequence', t`Sequence`],
    ['termid', t`Term`],
    ['address2', t`City`],
    ['address3', t`Region`],
    ['country', t`Country`],
    ['description', t`Description`],
    ['description_longdescription', t`Description detail`],
    ['status', t`Status`],
    ['status_description', t`Status label`],
    ['ponum', t`Purchase order`],
    ['prnum', t`Purchase requisition`],
    ['invusenum', t`Inventory usage`],
    ['resultnum', t`Inspection result`],
    ['revisionnum', t`Revision`],
    ['pluscjprevnum', t`Job-plan revision`],
    ['jpnum', t`Job plan`],
    ['pmnum', t`Preventive maintenance`],
    ['assetnum', t`Asset`],
    ['asset', t`Asset`],
    ['location', t`Location`],
    ['parent', t`Parent`],
    ['itemnum', t`Item`],
    ['itemsetid', t`Item set`],
    ['vendor', t`Vendor`],
    ['company', t`Company`],
    ['name', t`Name`],
    ['manufacturer', t`Manufacturer`],
    ['modelnum', t`Model`],
    ['serialnum', t`Serial number`],
    ['refwo', t`Work order reference`],
    ['wonum', t`Work order`],
    ['quantity', t`Quantity`],
    ['orderqty', t`Order quantity`],
    ['itemqty', t`Planned item quantity`],
    ['predjpnum', t`Predecessor job plan`],
    ['predjptask', t`Predecessor task`],
    ['orderunit', t`Order unit`],
    ['issueunit', t`Issue unit`],
    ['unitcost', t`Unit cost`],
    ['linecost', t`Line cost`],
    ['currencyunitcost', t`Unit cost in transaction currency`],
    ['currencylinecost', t`Line cost in transaction currency`],
    ['totalcost', t`Total cost`],
    ['currencycode', t`Currency`],
    ['curbal', t`Current balance`],
    ['curbaltotal', t`Storeroom total balance`],
    ['physcnt', t`Physical count`],
    ['physcntdate', t`Physical count date`],
    ['binnum', t`Bin`],
    ['frequency', t`Frequency`],
    ['frequnit', t`Frequency unit`],
    ['nextdate', t`Next scheduled date`],
    ['jptask', t`Job task`],
    ['craft', t`Craft`],
    ['laborhrs', t`Planned hours`],
    ['fromstoreloc', t`From storeroom`],
    ['frombin', t`From bin`],
    ['tobin', t`To bin`],
    ['issuetype', t`Transaction type`],
    ['usetype', t`Use type`],
    ['actualdate', t`Actual date`],
    ['transdate', t`Transaction date`],
    ['receivedqty', t`Received quantity`],
    ['returnedqty', t`Returned quantity`],
    ['txtresponse', t`Text response`],
    ['numresponse', t`Numeric response`],
    ['inspquestionnum', t`Inspection question number`],
    ['inspfieldnum', t`Inspection field`],
    ['assetattrid', t`Attribute`],
    ['alnvalue', t`Value`],
    ['numvalue', t`Numeric value`],
    ['inspformnum', t`Inspection form`],
    ['assettype_description', t`Asset type`],
    ['type_description', t`Location type`],
    ['category_description', t`Category`],
    ['worktype', t`Work type`],
    ['usetype_description', t`Use type`],
    ['naes_dwgnum', t`Drawing reference`],
    ['orderdate', t`Order date`],
    ['issuedate', t`Requisition issue date`],
    ['requireddate', t`Required date`],
    ['lastcompdate', t`Last recorded completion date`],
    ['receipts_description', t`Receipt state`],
    ['avblbalance', t`Available balance field`],
    ['minlevel', t`Minimum level`],
    ['maxlevel', t`Maximum level`],
    ['linetype', t`Line type`],
    ['catalogcode', t`Catalog code`],
    ['tasksequence', t`Task sequence`],
    ['skilllevel', t`Skill`],
    ['toolnum', t`Tool`],
    ['laborcode', t`Labor`],
    ['tostoreloc', t`To storeroom`],
    ['systemid', t`System`],
    ['ancestor', t`Ancestor`],
    ['measureunitid', t`Unit`],
    ['metername', t`Meter`],
    ['question', t`Question`],
    ['response', t`Selected response`],
    ['result', t`Result`],
    ['rejectqty', t`Rejected quantity`],
    ['issuedqty', t`Issued quantity`],
    ['taskduration', t`Task duration`],
    ['avgcost', t`Average cost`],
    ['lastcost', t`Last cost`],
    ['dateresponse', t`Response date`],
  ] as const
}

function relations() {
  return [
    ['poline', t`Ordered lines`],
    ['prline', t`Requested lines`],
    ['jobtask', t`Planned tasks`],
    ['joblabor', t`Planned labor`],
    ['jobmaterial', t`Planned materials`],
    ['jobtool', t`Planned tools`],
    ['jobservice', t`Planned services`],
    ['jobitem', t`Planned items`],
    ['invuseline', t`Material usage lines`],
    ['invuselinesplit', t`Usage splits`],
    ['inspfieldresult', t`Inspection responses`],
    ['inspfieldresultselection', t`Selected inspection options`],
    ['invbalances', t`Bin balances`],
    ['invcost', t`Inventory valuation`],
    ['matrectrans', t`Recorded receipt transactions`],
    ['matusetrans', t`Recorded material transactions`],
    ['inventory', t`Referenced inventory`],
    ['locations', t`Referenced locations`],
    ['lochierarchy', t`Location hierarchy`],
    ['locancestor', t`Location ancestors`],
    ['assetspec', t`Asset specifications`],
    ['locationspec', t`Location specifications`],
    ['pocost', t`Cost distributions`],
    ['prcost', t`Cost distributions`],
    ['poterm', t`Purchasing terms`],
    ['prterm', t`Requisition terms`],
    ['jptaskrelation', t`Task relationships`],
    ['assetmeter', t`Asset meters`],
    ['pmsequence', t`Planned job-plan sequences`],
    ['asset', t`Referenced assets`],
    ['pmeter', t`PM meters`],
    ['locationmeter', t`Location meters`],
    ['jobplan', t`Referenced job plans`],
    ['invvendor', t`Item vendors`],
    ['locsystem', t`Location systems`],
  ] as const
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function recordTypeLabels(): Record<CmmsRecordType, string> {
  return {
    'cmms-work-order': t`Work order`,
    'cmms-pm': t`Preventive maintenance`,
    'cmms-asset': t`Asset`,
    'cmms-item': t`Item`,
    'cmms-stock': t`Inventory stock`,
    'cmms-purchase-order': t`Purchase order`,
    'cmms-purchase-requisition': t`Purchase requisition`,
    'cmms-vendor': t`Vendor`,
    'cmms-location': t`Operating location`,
    'cmms-job-plan': t`Job plan`,
    'cmms-inspection-result': t`Inspection result`,
    'cmms-inventory-usage': t`Inventory usage`,
    'cmms-inventory-balance': t`Inventory balance`,
  }
}

const MONEY_FIELDS = new Set([
  'unitcost',
  'linecost',
  'totalcost',
  'avgcost',
  'lastcost',
  'currencyunitcost',
  'currencylinecost',
])

function moneyCurrency(
  row: Record<string, unknown>,
  path: string,
  inherited?: string
) {
  // Inventory transactions distinguish base amounts from their vendor-currency amounts.
  if (/\.(matrectrans|matusetrans)\.\d+$/.test(path)) return undefined
  return cmmsCurrency(row) ?? inherited
}

export async function buildCmmsRecordPdf(
  entry: WorkOrderEntry
): Promise<WorkOrderPdf> {
  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true })
  const { record, source } = entry.data
  const number = cmmsRecordNumber(entry)
  const sheet = new WorkOrderSheet(pdf, number)
  const evidence: WorkOrderPdf['evidence'] = []
  sheet.header(
    recordTypeLabels()[entry.type],
    record.description || number,
    source,
    {
      title: 'record.description',
      number: `record.${CMMS_NUMBER_FIELDS[entry.type]}`,
    }
  )
  if (record.status || record.status_description) {
    const status =
      record.status_description && record.status
        ? `${record.status_description} (${record.status})`
        : record.status_description || record.status
    sheet.status(`${t`Status`}: ${status}`, 'record.status')
  }
  sheet.y += 14
  const fields = businessFields()
  const children = relations()
  const canonical: Record<string, string[]> = sourceLabels
  const collectFields = (
    row: Record<string, unknown>,
    path: string,
    inheritedCurrency?: string
  ): WorkOrderField[] =>
    fields.flatMap(([key, label]) => {
      const value = row[key]
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        typeof value === 'object'
      )
        return []
      const text = workOrderPlainText(String(value))
      if (!text) return []
      const id =
        path === 'record' && key === 'status_description'
          ? 'record.status'
          : `${path}.${key}`
      const aliases = (canonical[key] ?? []).map(
        (sourceLabel) => `${sourceLabel} (${key}): ${text}`
      )
      if (path === 'record')
        aliases.push(
          ...(canonical[`root_${key}`] ?? []).map(
            (sourceLabel) => `${sourceLabel}: ${text}`
          )
        )
      evidence.push({ id, text: [text, ...aliases].join('\n') })
      return [
        {
          id,
          label:
            entry.type === 'cmms-vendor' && key === 'type_description'
              ? t`Category`
              : label,
          value: MONEY_FIELDS.has(key)
            ? formatCmmsMoney(
                text,
                key.startsWith('currency')
                  ? (cmmsCurrency(row) ?? inheritedCurrency)
                  : moneyCurrency(row, path, inheritedCurrency)
              )
            : text,
        },
      ]
    })
  const writeFields = (values: WorkOrderField[], root = false) => {
    const descriptions = values.filter(
      (field) =>
        field.id.endsWith('.description') ||
        field.id.endsWith('.description_longdescription')
    )
    const visibleDescriptions = root
      ? descriptions.filter(
          (field) =>
            field.id !== 'record.description' &&
            field.value !== workOrderPlainText(record.description)
        )
      : descriptions
    if (root && visibleDescriptions.length)
      sheet.section(t`Description`, undefined, 20)
    for (const field of visibleDescriptions)
      sheet.write(field.value, { field: field.id })
    const metadata = values.filter(
      (field) =>
        !descriptions.includes(field) &&
        !(
          root &&
          (field.id === `record.${CMMS_NUMBER_FIELDS[entry.type]}` ||
            field.id === 'record.status' ||
            field.id === 'record.status_description')
        )
    )
    if (root && metadata.length)
      sheet.section(
        t`Record details`,
        undefined,
        Math.min(80, sheet.fieldRowHeight(metadata.slice(0, 2)))
      )
    sheet.fields(metadata)
  }
  sheet.write(
    t`Imported source data. Plans and orders do not establish performed work; stock figures are snapshots.`,
    { size: 9, muted: true }
  )
  sheet.y += 8
  const rootFields = collectFields(record, 'record')
  if (
    workOrderPlainText(record.description_longdescription) ===
    workOrderPlainText(record.description)
  ) {
    for (const field of evidence) {
      if (field.id === 'record.description_longdescription')
        field.id = 'record.description'
    }
  }
  writeFields(rootFields, true)
  const writeChildren = (
    row: Record<string, unknown>,
    path: string,
    depth: number,
    inheritedCurrency?: string
  ) => {
    if (depth >= 3) return
    for (const [key, label] of children) {
      const rows = row[key]
      if (!Array.isArray(rows)) continue
      for (const [index, child] of rows.entries()) {
        if (!isRecord(child)) continue
        const childPath = `${path}.${key}.${index}`
        const childFields = collectFields(
          child,
          childPath,
          moneyCurrency(row, path, inheritedCurrency)
        )
        const hasDescription = childFields.some(
          (field) =>
            field.id.endsWith('.description') ||
            field.id.endsWith('.description_longdescription')
        )
        sheet.section(
          `${label} · ${index + 1}`,
          undefined,
          hasDescription
            ? 20
            : Math.min(80, sheet.fieldRowHeight(childFields.slice(0, 2)))
        )
        writeFields(childFields)
        writeChildren(
          child,
          childPath,
          depth + 1,
          moneyCurrency(row, path, inheritedCurrency)
        )
      }
    }
  }
  writeChildren(record, 'record', 0)
  sheet.ensure(170)
  sheet.section(t`Source record`)
  sheet.fields(
    [
      { id: 'sourceSystem', label: t`Source system`, value: source.system },
      { id: 'sourceSite', label: t`Site`, value: source.site },
      { id: 'sourceKey', label: t`Source key`, value: source.key },
      { id: 'sourceVersion', label: t`Source version`, value: source.version },
    ].filter((field) => field.value)
  )
  sheet.footer(source)
  return {
    blob: pdf.output('blob'),
    pageCount: pdf.getNumberOfPages(),
    runs: sheet.runs,
    evidence,
  }
}
